/**
 * 게임 월드 레이어 depth SSoT — scene에 직접 add되는 객체의 z-order를 한 곳에서 관리한다.
 *
 * Phaser는 depth가 클수록 앞(위)에 그려진다. 아래 순서가 그대로 화면 쌓임 순서다:
 * 배경(far<near) < 프레임 < 조준선/탭패널 < 발사대 < 벽돌(base<overlay<label) < 투사체 < 전환커버
 *   < 환생전환커버 < 전투타이머 < HUD/탭바 < 토스트 모달.
 *
 * 새 scene 직속 객체를 추가하거나 레이어 순서를 바꿀 땐 반드시 여기 값으로 비교/조정한다.
 *
 * 범위 밖(여기서 관리하지 않음):
 *  - TabView/패널 내부의 Container-local depth(1~5) — 각 패널 Container 안에서만 쓰는 상대 순서라
 *    이 전역 척도와 다른 네임스페이스다.
 *  - battle 탭 콘텐츠 레이어 — ModuleGridView/ModulePaletteView의 그리드 셀·드래그 오버레이
 *    depth(2~5, 50/60/61), BattleTabView의 안내 텍스트(pathText/specText, scene.add지만 depth 3).
 *    모두 게임뷰와 좌표가 분리된 탭 콘텐츠 영역이라 각 View가 로컬 관리한다.
 *    (brickBase 등 게임 월드 값과 숫자가 겹쳐도 게임 월드 레이어가 아니다 — z-fight 없음.)
 */
export const DEPTH = {
  // --- 게임 월드 (플레이필드) ---
  /** 전투 배경 원경 타일 */
  backgroundFar: -2.2,
  /** 전투 배경 근경 타일 */
  backgroundNear: -2,
  /** 게임뷰 9-slice 프레임 (배경 위, 콘텐츠 뒤) */
  frame: -1,
  /** 에임 직선 가이드 (벽돌·발사체 뒤) */
  aimLine: 0,
  /** 탭 콘텐츠 공통 패널 배경 */
  tabPanel: 0,
  /** 발사대 이미지 */
  launcher: 1,
  /** 벽돌 glow — base 뒤에서 스케일되지 않는 런타임 발광 */
  brickGlow: 2,
  /** 벽돌 base — 통일 도형 텍스처 tint */
  brickBase: 3,
  /** 벽돌 overlay — 룬 텍스처(base 풀커버) */
  brickOverlay: 4,
  /** 벽돌 label — HP 수치 텍스트 */
  brickLabel: 5,
  /** 투사체 — 벽돌 위로 렌더해 관통 중에도 가려지지 않게 한다 */
  projectile: 6,
  /** 조준 타겟 마커 — 적 본체/HP 라벨 위, 투사체와 피격 FX 아래에 둔다. */
  targetMarker: 5.6,
  /** 전투 피해/골드 플로팅 텍스트 */
  combatTextFx: 20,
  /** 전투 골드 플로팅 텍스트 배경 */
  combatGoldFxBg: 20.4,
  /** 전투 골드 플로팅 텍스트 아이콘/라벨 */
  combatGoldFx: 20.5,
  /** 가이드퀘스트 배너 배경 — 전투뷰 하위 오버레이라 전투/환생 전환 커버보다 아래 */
  guideQuestPanelBg: 21.6,
  /** 가이드퀘스트 배너 텍스트 */
  guideQuestPanelText: 21.7,
  /** 가이드퀘스트 배너 action 버튼 */
  guideQuestPanelButton: 21.8,
  /** 챕터 전환 페이드 커버 */
  chapterTransitionCover: 22,
  /** 환생 전환 페이드 커버 — 게임뷰 outer 전체와 던전 타이머까지 가린다 (HUD/탭바 아래) */
  rebirthTransitionCover: 28,
  /** 환생 전환 안내 텍스트 (커버 위) */
  rebirthTransitionText: 29,
  /** 전투 다이어로그 게임뷰 오버레이 패널 */
  dialoguePanel: 29.2,
  /** 전투 다이어로그 게임뷰 오버레이 텍스트/버튼 */
  dialogueContent: 29.3,

  // --- 오버레이 (월드 위, scene 직속) ---
  /** 일반 전투 패배 마지노선 — 위험 상태일 때 발사대 충돌 판정 위치를 게임뷰에 표시 */
  battleDeadlineLine: 24.8,
  /** 던전 제한시간 바 배경 */
  dungeonTimerBg: 25,
  /** 던전 제한시간 바 fill */
  dungeonTimerFill: 26,
  /** 던전 제한시간 라벨 */
  dungeonTimerLabel: 27,
  /** 일반 전투 블록 하강 progress 바 배경 */
  battlePressureBg: 25,
  /** 일반 전투 블록 하강 progress 바 fill */
  battlePressureFill: 26,
  /** 일반 전투 블록 하강 progress 라벨 */
  battlePressureLabel: 27,
  /** HUD 상단바 / 하단 탭바 배경 */
  hudBar: 30,
  /** HUD 콘텐츠(버튼·칩·아이콘·라벨) */
  hudContent: 31,
  /** dev 배너 미리보기 플레이스홀더 — 미리보기 모드 상단 reserve 영역 표시(HUD 위, 모달 아래). 실기기 OS 배너와 무관. */
  bannerPreview: 35,
  /** DEV 치트 앱 타이머 — HUD 위, 모달/토스트 아래에 고정 표시. */
  appTimerOverlay: 36,
  /** 펼친 드롭다운 뒤 전체화면 dim 캐처 — 탭바(hudBar/hudContent) 위, 모달 밴드(2000) 아래 */
  dropdownDim: 40,
  /** 펼친 드롭다운 본체(트리거 + 옵션 리스트) — dim 위. 모달(2000+)·토스트(3000+)보다는 아래라 모달을 가리지 않는다 */
  dropdownOverlay: 41,
  /** 토스트 모달 패널 — 모달 밴드 위(토스트는 항상 최상위 transient 알림) */
  toastPanel: 3000,
  /** 토스트 모달 텍스트 */
  toastText: 3001,
} as const;

/**
 * 오버레이 depth 밴드 SSoT — `DepthBands`(@yjsgames/phaser-ui/view-order)에 주입하는 스펙.
 * 상수 값은 여기(앱)가 소유하고, 겹침 검증·index→depth 계산은 패키지 메커니즘이 담당한다.
 *
 * - modal: ModalHost의 ViewStack이 push 순서대로 base+10씩 부여 — 팝업 위 팝업(reward→rewardDetail)
 *   같은 상대 순서를 add 순서가 아닌 명시 depth로 보장한다. [2000, 3000)
 * - toast: 모달 밴드 위 — 모달이 떠 있어도 토스트는 가려지지 않는다. [3000, 3010)
 */
export const UI_BANDS = {
  modal: { base: 2000, step: 10, capacity: 100 },
  toast: { base: 3000, step: 1, capacity: 10 },
} as const;
